import { useState, useMemo, useCallback, useRef } from "react";
import EmptyState from "./EmptyState";

const API      = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");
const authFetch = async (path, opts = {}) => {
  const res  = await fetch(API + path, { ...opts, headers: { "Content-Type":"application/json", Authorization:"Bearer "+getToken(), ...opts.headers } });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed");
  return data;
};

const INR = (v) => "₹" + Math.round(Number(v)||0).toLocaleString("en-IN");

const PLATFORMS = [
  { id:"shopify",  name:"Shopify",  color:"#5a8a00", bg:"#f0fdf4" },
  { id:"amazon",   name:"Amazon",   color:"#c45000", bg:"#fff3e0" },
  { id:"flipkart", name:"Flipkart", color:"#1b57c0", bg:"#eff6ff" },
  { id:"myntra",   name:"Myntra",   color:"#b5003c", bg:"#fff0f5" },
  { id:"meesho",   name:"Meesho",   color:"#9f2089", bg:"#fdf4ff" },
];

const COURIERS = [
  { id:"bluedart",   name:"Blue Dart",   rates:{ "0.5":45,"1":65,"2":95,"5":180 }, cod:40, tracking:"BD" },
  { id:"delhivery",  name:"Delhivery",   rates:{ "0.5":38,"1":55,"2":82,"5":160 }, cod:35, tracking:"DL" },
  { id:"dtdc",       name:"DTDC",        rates:{ "0.5":35,"1":50,"2":75,"5":145 }, cod:30, tracking:"DT" },
  { id:"ekart",      name:"Ekart",       rates:{ "0.5":32,"1":48,"2":70,"5":135 }, cod:28, tracking:"EK" },
  { id:"shiprocket", name:"Shiprocket",  rates:{ "0.5":29,"1":42,"2":65,"5":125 }, cod:25, tracking:"SR" },
  { id:"shadowfax",  name:"Shadowfax",   rates:{ "0.5":27,"1":40,"2":60,"5":115 }, cod:22, tracking:"SF" },
];

const TABS = [
  { id:"orders",   label:"🛒 All Orders"       },
  { id:"labels",   label:"🏷️ Print Labels"     },
  { id:"rates",    label:"🚚 Courier Rates"    },
  { id:"tracking", label:"📍 Tracking"         },
  { id:"cod",      label:"💵 COD Management"   },
  { id:"returns",  label:"↩️ Returns"          },
];

const S = {
  card:   { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:12 },
  hdr:    { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13, display:"flex", alignItems:"center", justifyContent:"space-between" },
  th:     { padding:"7px 12px", textAlign:"left", fontSize:10, fontWeight:700, color:"#9ca3af", background:"#f9fafb", whiteSpace:"nowrap" },
  td:     { padding:"7px 12px", fontSize:12, borderBottom:"1px solid #f3f4f6", verticalAlign:"middle" },
  btn:    (c="#F47920") => ({ padding:"7px 14px", borderRadius:7, fontSize:11, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
  btnOut: { padding:"6px 12px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
  input:  { padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit", outline:"none", boxSizing:"border-box" },
  label:  { fontSize:11, color:"#6b7280", fontWeight:600, display:"block", marginBottom:3 },
  badge:  (color, bg) => ({ background:bg, color, padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:700, whiteSpace:"nowrap" }),
};

function PlatBadge({ platform }) {
  const pl = PLATFORMS.find(p=>p.id===platform) || { name:platform, color:"#6b7280", bg:"#f3f4f6" };
  return <span style={{ ...S.badge(pl.color, pl.bg), fontSize:9 }}>{pl.name}</span>;
}

function StatusBadge({ status }) {
  const map = {
    fulfilled: { label:"Fulfilled", color:"#15803d", bg:"#f0fdf4" },
    partial:   { label:"Partial",   color:"#d97706", bg:"#fffbeb" },
    null:      { label:"Pending",   color:"#F47920", bg:"#fff8f2" },
    undefined: { label:"Pending",   color:"#F47920", bg:"#fff8f2" },
  };
  const s = map[status] || map["null"];
  return <span style={S.badge(s.color, s.bg)}>{s.label}</span>;
}

// Generate shipping label HTML
function generateLabelHTML(order, courier="Delhivery", awb="DL"+Date.now()) {
  const cust    = order.customer || {};
  const addr    = order.shipping_address || {};
  const name    = (cust.first_name||"") + " " + (cust.last_name||"");
  const items   = (order.line_items||[]).map(i=>i.title||i.name||"").join(", ") || order.name;
  const isCOD   = order.financial_status === "pending";
  return `<!DOCTYPE html><html><head><style>
    body{font-family:Arial,sans-serif;margin:0;padding:0}
    .label{width:10cm;border:2px solid #000;padding:10px;box-sizing:border-box}
    .header{display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #000;padding-bottom:8px;margin-bottom:8px}
    .logo{font-size:18px;font-weight:700;color:#F47920}
    .courier{font-size:14px;font-weight:700}
    .awb{font-size:20px;font-weight:700;text-align:center;border:2px solid #000;padding:8px;margin:8px 0;letter-spacing:3px}
    .to{margin:8px 0}
    .to-label{font-size:9px;color:#666;text-transform:uppercase}
    .to-name{font-size:14px;font-weight:700}
    .to-addr{font-size:11px;line-height:1.5}
    .cod{background:#000;color:#fff;text-align:center;padding:6px;font-weight:700;font-size:14px;margin:8px 0}
    .items{font-size:10px;color:#666;border-top:1px dashed #ccc;padding-top:6px;margin-top:6px}
    .footer{display:flex;justify-content:space-between;font-size:10px;margin-top:6px;border-top:1px solid #000;padding-top:6px}
  </style></head><body>
  <div class="label">
    <div class="header">
      <div class="logo">3June</div>
      <div class="courier">${courier}</div>
    </div>
    <div class="awb">${awb}</div>
    ${isCOD?`<div class="cod">💵 COD — ${INR(order.total_price||0)}</div>`:""}
    <div class="to">
      <div class="to-label">Deliver To</div>
      <div class="to-name">${name.trim()||"Customer"}</div>
      <div class="to-addr">${addr.address1||""}, ${addr.city||""}, ${addr.province||""} — ${addr.zip||""}</div>
      ${cust.phone||addr.phone?`<div class="to-addr"><strong>Ph:</strong> ${cust.phone||addr.phone}</div>`:""}
    </div>
    <div class="items">Items: ${items}</div>
    <div class="footer">
      <span>Order: ${order.name||""}</span>
      <span>Wt: 0.5 kg</span>
      <span>${new Date().toLocaleDateString("en-IN")}</span>
    </div>
  </div>
  </body></html>`;
}

export default function PageOrdersPro({ toast, orders=[], products=[], tenant, onSync }) {
  const [tab,         setTab]        = useState("orders");
  const [search,      setSearch]     = useState("");
  const [filterPlat,  setFilterPlat] = useState("all");
  const [filterStatus,setFilterStatus]= useState("all");
  const [filterDate,  setFilterDate] = useState("30"); // days
  const [selected,    setSelected]   = useState([]);
  const [rateForm,    setRateForm]   = useState({ from:"201001", to:"", weight:"0.5", cod:false, amount:"" });
  const [rates,       setRates]      = useState([]);
  const [trackAwb,    setTrackAwb]   = useState("");
  const [trackResult, setTrackResult]= useState(null);
  const [courier,     setCourier]    = useState("delhivery");
  const labelRef = useRef(null);

  // Filter orders
  const filtered = useMemo(() => {
    const cutoff = new Date(Date.now() - parseInt(filterDate)*24*60*60*1000);
    return orders.filter(o => {
      const q          = search.toLowerCase();
      const name       = ((o.customer?.first_name||"")+" "+(o.customer?.last_name||"")).toLowerCase();
      const matchSearch= !q || o.name?.toLowerCase().includes(q) || name.includes(q) || o.id?.toString().includes(q);
      const matchPlat  = filterPlat==="all" || (o._platform||"shopify")===filterPlat;
      const matchStatus= filterStatus==="all" ||
        (filterStatus==="pending"   && !o.fulfillment_status) ||
        (filterStatus==="fulfilled" && o.fulfillment_status==="fulfilled") ||
        (filterStatus==="cod"       && o.financial_status==="pending");
      const matchDate  = new Date(o.created_at||0) >= cutoff;
      return matchSearch && matchPlat && matchStatus && matchDate;
    });
  }, [orders, search, filterPlat, filterStatus, filterDate]);

  // Stats
  const totalOrders   = filtered.length;
  const pendingOrders = filtered.filter(o=>!o.fulfillment_status).length;
  const codOrders     = filtered.filter(o=>o.financial_status==="pending").length;
  const totalRevenue  = filtered.reduce((a,o)=>a+parseFloat(o.total_price||0),0);
  const codAmount     = filtered.filter(o=>o.financial_status==="pending").reduce((a,o)=>a+parseFloat(o.total_price||0),0);

  // Calculate courier rates
  const calcRates = () => {
    if (!rateForm.to) { toast("Enter delivery pincode","error"); return; }
    const weight = parseFloat(rateForm.weight)||0.5;
    const wKey   = weight<=0.5?"0.5":weight<=1?"1":weight<=2?"2":"5";
    const results = COURIERS.map(c => ({
      ...c,
      shipping: c.rates[wKey] || c.rates["5"],
      codFee:   rateForm.cod ? c.cod : 0,
      total:    (c.rates[wKey]||c.rates["5"]) + (rateForm.cod ? c.cod : 0),
      est:      Math.floor(Math.random()*2)+1 + "-" + (Math.floor(Math.random()*2)+3) + " days",
    })).sort((a,b)=>a.total-b.total);
    setRates(results);
    toast("Rates calculated ✓","success");
  };

  // Simulate tracking
  const trackShipment = () => {
    if (!trackAwb) { toast("Enter AWB number","error"); return; }
    const statuses = [
      { time:"Today 2:30 PM",   status:"Out for Delivery",    location:"Local Hub",       done:true  },
      { time:"Today 8:00 AM",   status:"In Transit",          location:"Delhi Hub",        done:true  },
      { time:"Yesterday 6 PM",  status:"Picked Up",           location:"Origin",           done:true  },
      { time:"Yesterday 2 PM",  status:"Manifested",          location:"Seller Location",  done:true  },
    ];
    setTrackResult({ awb:trackAwb, courier:trackAwb.substring(0,2).toUpperCase(), statuses, est:"Tomorrow by 8 PM" });
    toast("Tracking found ✓","success");
  };

  // Print label
  const printLabel = (order) => {
    const html   = generateLabelHTML(order, COURIERS.find(c=>c.id===courier)?.name||"Delhivery");
    const win    = window.open("","_blank","width=420,height=600");
    win.document.write(html);
    win.document.close();
    win.print();
  };

  // Print all selected labels
  const printAllLabels = () => {
    const selOrders = filtered.filter(o=>selected.includes(o.id||o.name));
    if (!selOrders.length) { toast("Select orders first","error"); return; }
    const combined = selOrders.map(o=>generateLabelHTML(o, COURIERS.find(c=>c.id===courier)?.name)).join('<div style="page-break-after:always"></div>');
    const win = window.open("","_blank");
    win.document.write(combined);
    win.document.close();
    win.print();
    toast(`${selOrders.length} labels sent to printer ✓`,"success");
  };

  // Export orders CSV
  const exportCSV = () => {
    const headers = ["Order","Date","Customer","Phone","Platform","Status","Amount","City","State","Items"];
    const rows    = filtered.map(o=>[
      o.name||o.id,
      new Date(o.created_at||0).toLocaleDateString("en-IN"),
      (o.customer?.first_name||"")+" "+(o.customer?.last_name||""),
      o.customer?.phone||o.shipping_address?.phone||"",
      o._platform||"shopify",
      o.fulfillment_status||"pending",
      o.total_price||0,
      o.shipping_address?.city||"",
      o.shipping_address?.province||"",
      (o.line_items||[]).map(i=>i.title||i.name||"").join("|"),
    ]);
    const csv = [headers,...rows].map(r=>r.map(v=>`"${v}"`).join(",")).join("\n");
    const a   = document.createElement("a"); a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv); a.download="orders.csv"; a.click();
    toast("Orders exported ✓","success");
  };

  const toggleSelect = (id) => setSelected(prev=>prev.includes(id)?prev.filter(x=>x!==id):[...prev,id]);
  const toggleAll    = (checked) => setSelected(checked ? filtered.map(o=>o.id||o.name) : []);

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:16 }}>🛒 Orders & Logistics</div>
          <div style={{ fontSize:12, color:"#9ca3af" }}>All platform orders, labels, courier rates, tracking and COD</div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button style={S.btn("#2563eb")} onClick={onSync}>↻ Sync Orders</button>
          <button style={S.btnOut} onClick={exportCSV}>⬇ Export CSV</button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="kpi-grid" style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10, marginBottom:14 }}>
        {[
          { label:"Total Orders",  value:totalOrders,        color:"#2563eb", icon:"🛒" },
          { label:"Pending",       value:pendingOrders,      color:"#F47920", icon:"⏳" },
          { label:"COD Orders",    value:codOrders,          color:"#7e22ce", icon:"💵" },
          { label:"COD Amount",    value:INR(codAmount),     color:"#dc2626", icon:"💰" },
          { label:"Total Revenue", value:INR(totalRevenue),  color:"#15803d", icon:"📈" },
        ].map(k=>(
          <div key={k.label} style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"11px 14px", borderLeft:"3px solid "+k.color, display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:20 }}>{k.icon}</span>
            <div>
              <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase" }}>{k.label}</div>
              <div style={{ fontSize:18, fontWeight:700, color:k.color }}>{k.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", borderBottom:"1px solid #e5e7eb", marginBottom:14, overflowX:"auto" }}>
        {TABS.map(t=>(
          <button key={t.id} style={{ padding:"9px 16px", border:"none", borderBottom:tab===t.id?"2px solid #F47920":"2px solid transparent", background:"none", color:tab===t.id?"#F47920":"#6b7280", fontWeight:tab===t.id?600:400, fontSize:12, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}
            onClick={()=>setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* ══ ALL ORDERS ══ */}
      {tab === "orders" && (
        <div>
          {/* Filters */}
          <div style={{ display:"flex", gap:8, marginBottom:12, flexWrap:"wrap", alignItems:"center" }}>
            <input style={{ ...S.input, width:220 }} placeholder="🔍 Search order, customer…" value={search} onChange={e=>setSearch(e.target.value)}/>
            <select style={{ ...S.input, width:"auto" }} value={filterPlat} onChange={e=>setFilterPlat(e.target.value)}>
              <option value="all">All Platforms</option>
              {PLATFORMS.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select style={{ ...S.input, width:"auto" }} value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="fulfilled">Fulfilled</option>
              <option value="cod">COD Only</option>
            </select>
            <select style={{ ...S.input, width:"auto" }} value={filterDate} onChange={e=>setFilterDate(e.target.value)}>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last 1 year</option>
            </select>
            <span style={{ fontSize:11, color:"#9ca3af" }}>{filtered.length} orders</span>
            {selected.length>0 && (
              <div style={{ display:"flex", gap:8, marginLeft:"auto" }}>
                <span style={{ fontSize:11, fontWeight:600, color:"#F47920", alignSelf:"center" }}>{selected.length} selected</span>
                <button style={S.btn()} onClick={printAllLabels}>🏷️ Print Labels</button>
                <button style={S.btnOut} onClick={()=>setSelected([])}>Clear</button>
              </div>
            )}
          </div>

          <div style={{ ...S.card, overflow:"hidden" }}>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead>
                  <tr>
                    <th style={S.th}><input type="checkbox" onChange={e=>toggleAll(e.target.checked)} checked={selected.length===filtered.length&&filtered.length>0}/></th>
                    {["Order","Date","Customer","Platform","Items","Amount","Status","COD","Actions"].map(h=><th key={h} style={S.th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length===0 ? (
                    <tr><td colSpan={10} style={{ padding:0 }}>
                      <EmptyState
                        icon="🛒"
                        title="No orders yet"
                        subtitle="Connect Shopify or your marketplaces and orders flow in here automatically — ready to process, invoice and ship."
                        columns={["Order","Customer","Platform","Items","Total","Status"]}
                        exampleRows={[
                          ["#1042","Mahesh Sigar","Shopify","2","₹2,598","Paid"],
                          ["#1041","Priya Mehta","Myntra","1","₹1,299","Shipped"],
                          ["#1040","Rahul Verma","Amazon","3","₹3,897","Pending"],
                        ]}
                      />
                    </td></tr>
                  ) : filtered.slice(0,100).map((o,i)=>{
                    const id      = o.id||o.name;
                    const name    = ((o.customer?.first_name||"")+" "+(o.customer?.last_name||"")).trim()||"Guest";
                    const items   = (o.line_items||[]).length || 1;
                    const isCOD   = o.financial_status==="pending";
                    return (
                      <tr key={id} style={{ background:i%2===0?"#fff":"#fafafa" }}
                        onMouseEnter={e=>e.currentTarget.style.background="#fff8f2"}
                        onMouseLeave={e=>e.currentTarget.style.background=i%2===0?"#fff":"#fafafa"}>
                        <td style={S.td}><input type="checkbox" checked={selected.includes(id)} onChange={()=>toggleSelect(id)}/></td>
                        <td style={{ ...S.td, fontFamily:"monospace", fontSize:10, color:"#F47920", fontWeight:600 }}>{o.name||o.id}</td>
                        <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{new Date(o.created_at||0).toLocaleDateString("en-IN",{day:"2-digit",month:"short"})}</td>
                        <td style={S.td}>
                          <div style={{ fontWeight:500, fontSize:12 }}>{name}</div>
                          <div style={{ fontSize:10, color:"#9ca3af" }}>{o.shipping_address?.city||""}</div>
                        </td>
                        <td style={S.td}><PlatBadge platform={o._platform||"shopify"}/></td>
                        <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{items} item{items!==1?"s":""}</td>
                        <td style={{ ...S.td, fontWeight:600 }}>{INR(o.total_price||0)}</td>
                        <td style={S.td}><StatusBadge status={o.fulfillment_status}/></td>
                        <td style={S.td}>
                          {isCOD && <span style={{ ...S.badge("#7e22ce","#fdf4ff"), marginRight:4 }}>COD</span>}
                        </td>
                        <td style={S.td}>
                          {o.trackingNumber ? (
                            <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                              <span style={{ fontFamily:"monospace", fontSize:10, color:"#2563eb" }}>{o.trackingNumber}</span>
                              {o.trackingUrl && (
                                <a href={o.trackingUrl} target="_blank" rel="noreferrer"
                                  style={{ fontSize:10, color:"#F47920", fontWeight:600, textDecoration:"none" }}
                                  title="Track shipment">📍</a>
                              )}
                            </div>
                          ) : (
                            <span style={{ fontSize:10, color:"#9ca3af" }}>—</span>
                          )}
                        </td>
                        <td style={S.td}>
                          <div style={{ display:"flex", gap:4 }}>
                            <button style={{ ...S.btn("#2563eb"), padding:"3px 8px", fontSize:10 }} onClick={()=>printLabel(o)} title="Print Label">🏷️</button>
                            <button style={{ ...S.btnOut, padding:"3px 8px", fontSize:10 }} title="View Details"
                              onClick={()=>{
                                const info = `Order: ${o.name}\nCustomer: ${name}\nPhone: ${o.customer?.phone||"—"}\nCity: ${o.shipping_address?.city||"—"}\nAmount: ${INR(o.total_price||0)}\nStatus: ${o.fulfillment_status||"Pending"}\nPayment: ${isCOD?"COD":"Prepaid"}`;
                                alert(info);
                              }}>👁</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ══ PRINT LABELS ══ */}
      {tab === "labels" && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
            <div style={S.card}>
              <div style={S.hdr}><span>⚙️ Label Settings</span></div>
              <div style={{ padding:16 }}>
                <div style={{ marginBottom:12 }}>
                  <label style={S.label}>Courier Partner</label>
                  <select style={{ ...S.input, width:"100%" }} value={courier} onChange={e=>setCourier(e.target.value)}>
                    {COURIERS.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div style={{ background:"#f9fafb", borderRadius:8, padding:12, fontSize:12, color:"#6b7280", marginBottom:12 }}>
                  ✅ Labels include: Order ID, AWB, Customer address, COD badge, Item list, Weight
                </div>
                <button style={{ ...S.btn(), width:"100%", marginBottom:8 }} onClick={printAllLabels} disabled={selected.length===0}>
                  🖨️ Print Selected Labels ({selected.length})
                </button>
                <button style={{ ...S.btnOut, width:"100%", fontSize:11 }} onClick={()=>setSelected(filtered.map(o=>o.id||o.name))}>
                  Select All Pending Orders
                </button>
              </div>
            </div>

            <div style={S.card}>
              <div style={S.hdr}><span>📋 Pending Orders ({pendingOrders})</span></div>
              <div style={{ overflowY:"auto", maxHeight:300 }}>
                {filtered.filter(o=>!o.fulfillment_status).map((o,i)=>{
                  const id   = o.id||o.name;
                  const name = ((o.customer?.first_name||"")+" "+(o.customer?.last_name||"")).trim()||"Guest";
                  return (
                    <div key={id} style={{ padding:"8px 16px", borderBottom:"1px solid #f3f4f6", display:"flex", alignItems:"center", gap:10 }}>
                      <input type="checkbox" checked={selected.includes(id)} onChange={()=>toggleSelect(id)}/>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:12, fontWeight:500 }}>{name}</div>
                        <div style={{ fontSize:10, color:"#9ca3af" }}>{o.name} · {INR(o.total_price||0)}</div>
                      </div>
                      <button style={{ ...S.btn(), padding:"3px 8px", fontSize:10 }} onClick={()=>printLabel(o)}>🏷️ Print</button>
                    </div>
                  );
                })}
                {pendingOrders===0 && <div style={{ padding:24, textAlign:"center", color:"#9ca3af" }}>✅ No pending orders</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ COURIER RATES ══ */}
      {tab === "rates" && (
        <div>
          <div style={{ ...S.card, padding:16, marginBottom:14 }}>
            <div style={{ fontWeight:700, fontSize:13, marginBottom:12 }}>🔍 Compare Courier Rates</div>
            <div className="courier-rate-form" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr auto", gap:10, alignItems:"flex-end" }}>
              <div>
                <label style={S.label}>From Pincode</label>
                <input style={{ ...S.input, width:"100%" }} placeholder="201001" value={rateForm.from} onChange={e=>setRateForm(f=>({...f,from:e.target.value}))}/>
              </div>
              <div>
                <label style={S.label}>To Pincode</label>
                <input style={{ ...S.input, width:"100%" }} placeholder="400001" value={rateForm.to} onChange={e=>setRateForm(f=>({...f,to:e.target.value}))}/>
              </div>
              <div>
                <label style={S.label}>Weight (kg)</label>
                <select style={{ ...S.input, width:"100%" }} value={rateForm.weight} onChange={e=>setRateForm(f=>({...f,weight:e.target.value}))}>
                  {["0.5","1","2","5"].map(w=><option key={w} value={w}>{w} kg</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Payment</label>
                <select style={{ ...S.input, width:"100%" }} value={rateForm.cod?"cod":"prepaid"} onChange={e=>setRateForm(f=>({...f,cod:e.target.value==="cod"}))}>
                  <option value="prepaid">Prepaid</option>
                  <option value="cod">COD</option>
                </select>
              </div>
              <button style={{ ...S.btn(), padding:"8px 20px" }} onClick={calcRates}>Compare</button>
            </div>
          </div>

          {rates.length>0 && (
            <div style={{ ...S.card, overflow:"hidden" }}>
              <div style={S.hdr}><span>📊 Rate Comparison — {rateForm.from} → {rateForm.to} · {rateForm.weight}kg · {rateForm.cod?"COD":"Prepaid"}</span></div>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead><tr>{["","Courier","Shipping","COD Fee","Total","Est. Delivery",""].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {rates.map((r,i)=>(
                    <tr key={r.id} style={{ background:i===0?"#fff8f2":i%2===0?"#fff":"#fafafa" }}>
                      <td style={S.td}>{i===0?<span style={{ fontSize:10, fontWeight:700, color:"#15803d", background:"#f0fdf4", padding:"1px 6px", borderRadius:8 }}>✨ Best</span>:""}</td>
                      <td style={{ ...S.td, fontWeight:600, color:i===0?"#F47920":"#374151" }}>{r.name}</td>
                      <td style={{ ...S.td, textAlign:"right" }}>{INR(r.shipping)}</td>
                      <td style={{ ...S.td, textAlign:"right", color:r.codFee>0?"#7e22ce":"#9ca3af" }}>{r.codFee>0?INR(r.codFee):"—"}</td>
                      <td style={{ ...S.td, textAlign:"right", fontWeight:700, color:i===0?"#F47920":"#374151" }}>{INR(r.total)}</td>
                      <td style={{ ...S.td, color:"#6b7280" }}>{r.est}</td>
                      <td style={S.td}><button style={{ ...S.btn(i===0?"#F47920":"#9ca3af"), padding:"3px 8px", fontSize:10 }} onClick={()=>{ setCourier(r.id); setTab("labels"); toast(r.name+" selected","success"); }}>Select</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══ TRACKING ══ */}
      {tab === "tracking" && (() => {
        const fulfilledOrders = orders.filter(o => o.fulfillment_status === "fulfilled" || o.trackingNumber);
        const untracked       = orders.filter(o => o.fulfillment_status === "fulfilled" && !o.trackingNumber);
        const withTracking    = orders.filter(o => o.trackingNumber);

        // Courier URL builder
        const buildTrackUrl = (o) => {
          if (o.trackingUrl) return o.trackingUrl;
          const awb = o.trackingNumber || "";
          const co  = (o.trackingCompany || "").toLowerCase();
          if (co.includes("delhivery"))  return `https://www.delhivery.com/track/package/${awb}`;
          if (co.includes("blue dart") || co.includes("bluedart")) return `https://www.bluedart.com/tracking?trackFor=0&trackNo=${awb}`;
          if (co.includes("dtdc"))       return `https://www.dtdc.in/tracking.asp?Tracking_no=${awb}`;
          if (co.includes("ekart") || co.includes("ek_e2e")) return `https://ekartlogistics.com/shipmenttrack/${awb}`;
          if (co.includes("xpressbees"))return `https://www.xpressbees.com/shipment/tracking?awbNo=${awb}`;
          if (co.includes("shiprocket")) return `https://shiprocket.co/tracking/${awb}`;
          if (co.includes("shadowfax")) return `https://tracker.shadowfax.in/?number=${awb}`;
          if (co.includes("india post") || co.includes("indiapost")) return `https://www.indiapost.gov.in/vas/pages/trackConsignment.aspx?Consignment=${awb}`;
          return `https://www.google.com/search?q=track+${awb}+${encodeURIComponent(co)}`;
        };

        return (
          <div>
            {/* KPI strip */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:14 }}>
              {[
                { label:"Fulfilled Orders", value:fulfilledOrders.length, color:"#15803d" },
                { label:"With Tracking",    value:withTracking.length,    color:"#2563eb" },
                { label:"No Tracking AWB",  value:untracked.length,       color:"#d97706" },
                { label:"Tracking Rate",    value:fulfilledOrders.length>0?Math.round(withTracking.length/fulfilledOrders.length*100)+"%":"—", color:"#F47920" },
              ].map(k=>(
                <div key={k.label} style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"12px 14px", borderLeft:"3px solid "+k.color }}>
                  <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase" }}>{k.label}</div>
                  <div style={{ fontSize:20, fontWeight:700, color:k.color, marginTop:2 }}>{k.value}</div>
                </div>
              ))}
            </div>

            {/* AWB lookup */}
            <div style={{ ...S.card, padding:14, marginBottom:12 }}>
              <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                <div style={{ fontSize:13, fontWeight:600, flexShrink:0 }}>🔍 Quick Track:</div>
                <input style={{ ...S.input, flex:1 }} placeholder="Enter AWB / tracking number and press Enter…"
                  value={trackAwb} onChange={e=>setTrackAwb(e.target.value)}
                  onKeyDown={e=>{
                    if (e.key==="Enter" && trackAwb.trim()) {
                      const found = orders.find(o=>o.trackingNumber===trackAwb.trim());
                      const url   = found ? buildTrackUrl(found) : `https://www.google.com/search?q=track+shipment+${trackAwb.trim()}`;
                      window.open(url, "_blank");
                    }
                  }}/>
                <button style={S.btn()} onClick={()=>{
                  if (!trackAwb.trim()) return;
                  const found = orders.find(o=>o.trackingNumber===trackAwb.trim());
                  const url   = found ? buildTrackUrl(found) : `https://www.google.com/search?q=track+shipment+${trackAwb.trim()}`;
                  window.open(url, "_blank");
                }}>Track →</button>
              </div>
            </div>

            {/* Shipments table */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              {/* With tracking */}
              <div style={{ ...S.card, overflow:"hidden" }}>
                <div style={S.hdr}><span>✅ Shipments with Tracking ({withTracking.length})</span></div>
                {withTracking.length===0 ? (
                  <div style={{ padding:30, textAlign:"center", color:"#9ca3af", fontSize:12 }}>
                    No tracking numbers found.<br/>Fulfill orders in Shopify to get AWB numbers.
                  </div>
                ) : (
                  <div style={{ overflowY:"auto", maxHeight:460 }}>
                    {withTracking.sort((a,b)=>new Date(b.fulfilledAt||b.created_at)-new Date(a.fulfilledAt||a.created_at)).map((o,i)=>{
                      const trackUrl = buildTrackUrl(o);
                      const custName = ((o.customer?.first_name||"")+" "+(o.customer?.last_name||"")).trim()||"Guest";
                      return (
                        <div key={o.id||o.name} style={{ padding:"10px 14px", borderBottom:"1px solid #f3f4f6", background:i%2?"#fafafa":"#fff" }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4 }}>
                            <div>
                              <span style={{ fontFamily:"monospace", fontSize:11, fontWeight:600, color:"#F47920" }}>{o.name||o.id}</span>
                              <span style={{ fontSize:11, color:"#6b7280", marginLeft:8 }}>{custName}</span>
                            </div>
                            <span style={{ fontSize:10, color:"#9ca3af" }}>{o.fulfilledAt?.slice(0,10)||o.created_at?.slice(0,10)||"—"}</span>
                          </div>
                          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                            <div>
                              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                                <span style={{ fontFamily:"monospace", fontSize:12, fontWeight:700, color:"#2563eb" }}>{o.trackingNumber}</span>
                                {o.trackingCompany && <span style={{ fontSize:10, background:"#eff6ff", color:"#2563eb", padding:"1px 6px", borderRadius:6, fontWeight:600 }}>{o.trackingCompany}</span>}
                              </div>
                              <div style={{ fontSize:10, color:"#6b7280", marginTop:2 }}>
                                {o.shipping_address?.city||""}{o.shipping_address?.province?", "+o.shipping_address.province:""}
                              </div>
                            </div>
                            <a href={trackUrl} target="_blank" rel="noreferrer"
                              style={{ ...S.btn("#F47920"), textDecoration:"none", fontSize:11, padding:"5px 12px", display:"inline-block" }}>
                              📍 Track
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Without tracking */}
              <div style={{ ...S.card, overflow:"hidden" }}>
                <div style={S.hdr}><span>⚠️ Fulfilled — No Tracking ({untracked.length})</span></div>
                {untracked.length===0 ? (
                  <div style={{ padding:30, textAlign:"center", color:"#15803d", fontSize:12 }}>
                    ✅ All fulfilled orders have tracking numbers
                  </div>
                ) : (
                  <div style={{ overflowY:"auto", maxHeight:460 }}>
                    {untracked.map((o,i)=>{
                      const custName = ((o.customer?.first_name||"")+" "+(o.customer?.last_name||"")).trim()||"Guest";
                      return (
                        <div key={o.id||o.name} style={{ padding:"10px 14px", borderBottom:"1px solid #f3f4f6", background:i%2?"#fafafa":"#fff" }}>
                          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:2 }}>
                            <span style={{ fontFamily:"monospace", fontSize:11, fontWeight:600, color:"#F47920" }}>{o.name||o.id}</span>
                            <span style={{ fontSize:10, color:"#9ca3af" }}>{o.created_at?.slice(0,10)||"—"}</span>
                          </div>
                          <div style={{ fontSize:11, color:"#374151" }}>{custName}</div>
                          <div style={{ fontSize:10, color:"#d97706", marginTop:3 }}>
                            ⚠ Fulfilled in Shopify but no AWB number found
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {untracked.length > 0 && (
                  <div style={{ padding:"10px 14px", borderTop:"1px solid #e5e7eb", background:"#fffbeb", fontSize:11, color:"#92400e" }}>
                    💡 Add tracking numbers in Shopify Admin → Orders → Fulfillments
                  </div>
                )}
              </div>
            </div>

            {/* Courier breakdown */}
            {withTracking.length > 0 && (() => {
              const courierMap = {};
              withTracking.forEach(o => {
                const co = o.trackingCompany || "Unknown";
                courierMap[co] = (courierMap[co]||0) + 1;
              });
              return (
                <div style={{ ...S.card, marginTop:12 }}>
                  <div style={S.hdr}><span>🚚 Courier Breakdown</span></div>
                  <div style={{ padding:14, display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
                    {Object.entries(courierMap).sort((a,b)=>b[1]-a[1]).map(([co,count])=>(
                      <div key={co} style={{ background:"#f9fafb", borderRadius:8, padding:"10px 12px" }}>
                        <div style={{ fontWeight:600, fontSize:12, marginBottom:2 }}>{co}</div>
                        <div style={{ fontSize:22, fontWeight:700, color:"#2563eb" }}>{count}</div>
                        <div style={{ fontSize:10, color:"#9ca3af" }}>{Math.round(count/withTracking.length*100)}% of shipments</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        );
      })()}

      {/* ══ COD MANAGEMENT ══ */}
      {tab === "cod" && (
        <div>
          {/* COD KPIs */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:14 }}>
            {[
              { label:"COD Orders",      value:codOrders,                                                     color:"#7e22ce" },
              { label:"COD Amount",      value:INR(codAmount),                                                color:"#dc2626" },
              { label:"Prepaid Orders",  value:filtered.filter(o=>o.financial_status!=="pending").length,    color:"#15803d" },
              { label:"COD %",           value:totalOrders>0?Math.round(codOrders/totalOrders*100)+"%":"0%", color:"#F47920" },
            ].map(k=>(
              <div key={k.label} style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"12px 14px", borderLeft:"3px solid "+k.color }}>
                <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase" }}>{k.label}</div>
                <div style={{ fontSize:20, fontWeight:700, color:k.color }}>{k.value}</div>
              </div>
            ))}
          </div>

          <div style={{ ...S.card, overflow:"hidden" }}>
            <div style={S.hdr}><span>💵 COD Orders</span></div>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr>{["Order","Date","Customer","Phone","City","Amount","Status",""].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {filtered.filter(o=>o.financial_status==="pending").length===0 ? (
                  <tr><td colSpan={8} style={{ ...S.td, textAlign:"center", padding:40, color:"#9ca3af" }}>No COD orders in this period</td></tr>
                ) : filtered.filter(o=>o.financial_status==="pending").map((o,i)=>(
                  <tr key={o.id||o.name} style={{ background:i%2?"#fafafa":"#fff" }}>
                    <td style={{ ...S.td, fontFamily:"monospace", fontSize:10, color:"#F47920" }}>{o.name}</td>
                    <td style={{ ...S.td, fontSize:11 }}>{new Date(o.created_at||0).toLocaleDateString("en-IN",{day:"2-digit",month:"short"})}</td>
                    <td style={S.td}>{((o.customer?.first_name||"")+" "+(o.customer?.last_name||"")).trim()||"Guest"}</td>
                    <td style={{ ...S.td, fontFamily:"monospace", fontSize:11 }}>{o.customer?.phone||"—"}</td>
                    <td style={S.td}>{o.shipping_address?.city||"—"}</td>
                    <td style={{ ...S.td, fontWeight:700, color:"#7e22ce" }}>{INR(o.total_price||0)}</td>
                    <td style={S.td}><StatusBadge status={o.fulfillment_status}/></td>
                    <td style={S.td}><button style={{ ...S.btn("#15803d"), padding:"3px 8px", fontSize:10 }} onClick={()=>printLabel(o)}>🏷️ Label</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══ RETURNS ══ */}
      {tab === "returns" && (
        <div>
          <div style={{ ...S.card, padding:16 }}>
            <div style={{ fontWeight:700, fontSize:14, marginBottom:8 }}>↩️ Returns Management</div>
            <div style={{ fontSize:12, color:"#6b7280", marginBottom:16 }}>Track and manage product returns across all platforms</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:16 }}>
              {[
                { label:"Fulfilled Orders", value:filtered.filter(o=>o.fulfillment_status==="fulfilled").length, color:"#15803d" },
                { label:"Return Rate (Est)", value:"2-5%", color:"#d97706" },
                { label:"COD Refusals (Est)", value:Math.round(codOrders*0.08), color:"#dc2626" },
              ].map(k=>(
                <div key={k.label} style={{ background:"#f9fafb", borderRadius:8, padding:"12px 14px", textAlign:"center" }}>
                  <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase" }}>{k.label}</div>
                  <div style={{ fontSize:22, fontWeight:700, color:k.color, marginTop:4 }}>{k.value}</div>
                </div>
              ))}
            </div>
            <div style={{ background:"#eff6ff", borderRadius:8, padding:"10px 14px", fontSize:12, color:"#1e40af" }}>
              💡 For detailed returns management, use the Returns module in the Procurement section. This view shows return estimates based on your order data.
            </div>
          </div>

          {/* Fulfilled orders that could have returns */}
          <div style={{ ...S.card, overflow:"hidden" }}>
            <div style={S.hdr}><span>📦 Recently Fulfilled (Possible Returns)</span></div>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr>{["Order","Date","Customer","Platform","Amount","Fulfilled"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {filtered.filter(o=>o.fulfillment_status==="fulfilled").slice(0,20).map((o,i)=>(
                  <tr key={o.id||o.name} style={{ background:i%2?"#fafafa":"#fff" }}>
                    <td style={{ ...S.td, fontFamily:"monospace", fontSize:10, color:"#F47920" }}>{o.name}</td>
                    <td style={{ ...S.td, fontSize:11 }}>{new Date(o.created_at||0).toLocaleDateString("en-IN",{day:"2-digit",month:"short"})}</td>
                    <td style={S.td}>{((o.customer?.first_name||"")+" "+(o.customer?.last_name||"")).trim()||"Guest"}</td>
                    <td style={S.td}><PlatBadge platform={o._platform||"shopify"}/></td>
                    <td style={{ ...S.td, fontWeight:600 }}>{INR(o.total_price||0)}</td>
                    <td style={S.td}><span style={S.badge("#15803d","#f0fdf4")}>Fulfilled</span></td>
                  </tr>
                ))}
                {filtered.filter(o=>o.fulfillment_status==="fulfilled").length===0 && (
                  <tr><td colSpan={6} style={{ ...S.td, textAlign:"center", padding:30, color:"#9ca3af" }}>No fulfilled orders in this period</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
